/**
 * Post-auth redirect decision tree.
 *
 *   1. sessionToken set (desktop pairing flow) → /link
 *   2. has at least one linked YouTube profile     → /profile
 *   3. welcomeState.completedAt set                → /profile
 *   4. welcomeState.skippedAt set                  → /profile
 *   5. otherwise (brand new account)               → /welcome
 *
 * Wrapped in try/catch — a GraphQL outage shouldn't strand
 * a freshly-authenticated parent on the auth screen. Falls back
 * to /profile (which has its own empty-state with a setup link).
 *
 * Extracted from AuthPage so the landing page can call it directly
 * — a signed-in returning visitor clicking "Get started" goes
 * straight to the right post-login page instead of flashing
 * through /auth.
 */
import {client} from '../../api/graphql';
import {GET_WELCOME_BUNDLE_QUERY} from '../../api/graphql_welcome';
import {CURRENT_GUARDIAN} from '../../api/graphql_guardian_name';

export async function decidePostAuthRoute(
  sessionToken: string | null,
  pairCode?: string | null,
): Promise<string> {
  // Device pairing (desktop/HA parent login): return to the pair page to
  // claim the code now that we're authenticated.
  if (pairCode) {
    return `/pair?code=${encodeURIComponent(pairCode)}`;
  }
  if (sessionToken) {
    return `/link?sessionToken=${encodeURIComponent(sessionToken)}`;
  }
  // Pending invite: the recipient bounced through phone auth mid-redemption.
  // Route back to /invite (it reads the stashed token, redeems for the
  // now-signed-in user, and shows the app-download screen). This MUST win
  // over the profile/welcome routing below — otherwise a brand-new co-parent
  // lands on the "add a profile" flow before their invited kids are linked,
  // which is exactly the reported bug.
  try {
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('pendingInviteToken')
    ) {
      return '/invite';
    }
  } catch {
    // sessionStorage unavailable (private mode edge cases) — fall through.
  }
  try {
    const {data} = await client.query<{
      getCurrentUser: {welcomeState: {completedAt: number | null; skippedAt: number | null} | null} | null;
      getMyYouTubeProfiles: Array<{id: string}> | null;
    }>({
      query: GET_WELCOME_BUNDLE_QUERY,
      fetchPolicy: 'network-only',
    });
    const profileCount = data?.getMyYouTubeProfiles?.length ?? 0;
    const ws = data?.getCurrentUser?.welcomeState ?? null;
    // Compute where this parent would normally land…
    let dest: string;
    if (profileCount > 0) dest = '/profile';
    else if (ws?.completedAt || ws?.skippedAt) dest = '/profile';
    else dest = '/welcome';

    // …then gate it behind the display-name prompt if they have no name.
    // Phone-auth accounts start nameless; we force one before proceeding,
    // then continue to exactly where they were headed via ?next=. This runs
    // only on the non-invite branch (the pendingInviteToken early-return
    // above still wins — invite redemption links profiles first, and those
    // co-parents can set their name on a later pass).
    try {
      const {data: guardian} = await client.query<{
        getCurrentUser: {displayName: string | null} | null;
      }>({
        query: CURRENT_GUARDIAN,
        fetchPolicy: 'network-only',
      });
      const displayName = guardian?.getCurrentUser?.displayName?.trim();
      if (!displayName) {
        return `/name?next=${encodeURIComponent(dest)}`;
      }
    } catch (nameErr) {
      // Name lookup failed — don't strand the parent; let them proceed.
      console.warn('[postAuthRoute] display-name check failed, skipping gate:', nameErr);
    }

    return dest;
  } catch (err) {
    console.warn('[postAuthRoute] welcome-bundle fetch failed, falling back to /profile:', err);
    return '/profile';
  }
}
